import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_BASE;
  if (env && env.trim()) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
    return isLocal ? 'http://localhost:5001' : '';
};
const API_BASE = resolveApiBase();


export default function OrdenWebDetalle() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/web/pedidos/id/${id}`, { signal: abort.signal, credentials: 'include' });
        let j = null;
        try { j = await r.json(); } catch(e) {
          throw new Error(`Respuesta inválida del servidor (${r.status})`);
        }
        if (!r.ok || j.ok === false) throw new Error(j?.msg || `HTTP ${r.status}`);
        setData(j.pedido);
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message || 'Error cargando detalle');
      } finally {
        setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [id]);

  if (loading) return <div className="p-4">Cargando…</div>;
  if (err)     return <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded">{err}</div>;
  if (!data)   return <div className="p-4">Sin datos.</div>;

  // Preparar datos del pedido para vista legible
  const parsePedido = (p) => {
    if (!p) return null;
    if (typeof p === 'string') {
      try { return JSON.parse(p); } catch { return null; }
    }
    return p;
  };
  const pedidoObj = parsePedido(data.pedido) || {};
  const items = Array.isArray(pedidoObj.items) ? pedidoObj.items : [];
  const subtotal = Number(pedidoObj.subtotal ?? 0);
  const envio = Number(pedidoObj.envio ?? 0);
  const totalFinal = Number(pedidoObj.totalFinal ?? data.total ?? (subtotal + envio));
  const Q = (n) => `Q${(Number(n) || 0).toFixed(2)}`;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Pedido Web #{data.id}</h1>
        <Link to="/admin/ordenes-web" className="text-sky-700">← Volver</Link>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Cliente">
          <Item k="Código" v={data.codigo} />
          <Item k="Nombre" v={data.cliente} />
          <Item k="DPI" v={data.dpiCliente} />
        </Card>

        <Card title="Pago y Total">
          <Item k="Banco" v={data.banco} />
          <Item k="Boleta" v={data.codigo?.startsWith('WEB-') ? '—' : data.codigo} />
          <Item k="Total" v={Q(totalFinal)} />
        </Card>

        <Card title="Entrega">
          <Item k="Dirección" v={data.direccionEntrega} />
          <Item k="Fecha" v={new Date(data.created_at).toLocaleString()} />
          <Item k="Estado" v={data.estado} />
        </Card>

        <Card title="Comprobante / Imagen">
          {data.imagen ? (
            <img src={data.imagen.startsWith('http') ? data.imagen : (`${API_BASE}${data.imagen}`)} alt="Comprobante" className="max-h-72 rounded border" />
          ) : <div className="text-gray-500">Sin imagen</div>}
        </Card>
      </div>

      <Card title="Productos del pedido">
        {items.length === 0 ? (
          <div className="text-sm text-gray-500">Sin items</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Producto</th>
                  <th className="py-2 pr-4">Precio</th>
                  <th className="py-2 pr-4">Cantidad</th>
                  <th className="py-2 pr-0 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const precio = Number(it.precio || 0);
                  const qty = Number(it.qty || it.cantidad || 0);
                  const sub = precio * qty;
                  return (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-800">{it.nombre || 'Producto'}</td>
                      <td className="py-2 pr-4">{Q(precio)}</td>
                      <td className="py-2 pr-4">{qty}</td>
                      <td className="py-2 pr-0 text-right font-semibold">{Q(sub)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="pt-4 pr-4 text-right text-gray-600">Subtotal</td>
                  <td className="pt-4 text-right font-semibold">{Q(subtotal || items.reduce((a, it) => a + (Number(it.precio||0) * Number(it.qty||it.cantidad||0)), 0))}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="py-1 pr-4 text-right text-gray-600">Envío</td>
                  <td className="py-1 text-right font-semibold">{Q(envio)}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="pt-1 pr-4 text-right text-gray-800">Total</td>
                  <td className="pt-1 text-right font-bold">{Q(totalFinal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Item({ k, v }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <div className="text-gray-500">{k}</div>
      <div className="font-medium text-gray-800 text-right ml-4">{v ?? '—'}</div>
    </div>
  );
}
